import { useCallback, useEffect, useRef, useState } from 'react';

import {
  releaseTemplatePreviewSlot,
  takeTemplatePreviewSlot,
  TEMPLATE_PREVIEW_HOVER_INTENT_MS,
} from '../_components/template-ui/templatePreviewSlot';

/**
 * 网格模板卡「悬停预览视频」的状态与事件封装。
 *
 * ## 背景
 * 多卡片 hover 时若各自挂载带 `src` 的 `<video>`，会并发拉流、占满带宽与解码；鼠标滑过并不等于「要看预览」。
 *
 * ## 原理（与 {@link templatePreviewSlot} 配套）
 * 1. **全局互斥槽位**：全列表共用一把锁；新卡调用 `take` 时先执行上一卡注册的 teardown，保证**任意时刻至多一路**预览在加载/播放。
 * 2. **Teardown**：`pause`、`removeAttribute('src')`、`load()`，尽量打断当前元素上的媒体请求（HTMLVideoElement 无 Fetch Abort，此为常规手段）。
 * 3. **Hover intent**：封面已就绪且指针在卡内后，再延迟 {@link TEMPLATE_PREVIEW_HOVER_INTENT_MS}ms 才 `take` 并挂 `src`——等价于对「开始预览」做 debounce，减少快速划过时无效请求。
 * 4. **封面优先**：`previewUrl` 仅在封面 load/ready 之后才可能进入上述流程，避免灰底上先打大视频。
 * 5. **离开 / 卸载**：`release` 清槽并本地 teardown；组件 unmount 同样 `release`，避免槽位指向已卸载实例。
 *
 * UI 层（如 {@link TemplateCard}）只负责把返回的 ref、布尔态和回调接到 `img`/`video` 上即可。
 */
export function useTemplateCardHoverPreview(options: {
  coverUrl: string;
  previewUrl: string;
}) {
  const { coverUrl, previewUrl } = options;
  const videoRef = useRef<HTMLVideoElement>(null);
  const coverRef = useRef<HTMLImageElement>(null);
  const [isVideoActive, setIsVideoActive] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [shouldAttachVideo, setShouldAttachVideo] = useState(false);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [isCoverReady, setIsCoverReady] = useState(false);
  const [hasVideoError, setHasVideoError] = useState(false);

  const showVideo = isVideoReady && isVideoActive;
  const showLoadingOverlay = isVideoActive && !isVideoReady;

  const teardownPreviewVideo = useCallback(() => {
    const v = videoRef.current;
    if (v) {
      v.pause();
      v.currentTime = 0;
      v.removeAttribute('src');
      v.load();
    }
    setShouldAttachVideo(false);
    setIsVideoActive(false);
    setIsVideoReady(false);
    setHasVideoError(false);
  }, []);

  const beginPreviewAfterHoverIntent = useCallback(() => {
    takeTemplatePreviewSlot(teardownPreviewVideo);
    setIsVideoActive(true);
    setShouldAttachVideo(true);
    setIsVideoReady(false);
    setHasVideoError(false);
  }, [teardownPreviewVideo]);

  useEffect(() => {
    if (!isHovering || !isCoverReady) {
      setIsVideoActive(false);
      if (!isCoverReady) {
        setShouldAttachVideo(false);
      }
      return undefined;
    }

    const timerId = window.setTimeout(
      beginPreviewAfterHoverIntent,
      TEMPLATE_PREVIEW_HOVER_INTENT_MS,
    );
    return () => window.clearTimeout(timerId);
  }, [isCoverReady, isHovering, beginPreviewAfterHoverIntent]);

  useEffect(() => {
    if (!isVideoActive || !videoRef.current || !shouldAttachVideo) return;
    const video = videoRef.current;
    video.play().catch(() => {
      video.load();
    });
  }, [isVideoActive, shouldAttachVideo, previewUrl]);

  useEffect(() => {
    return () => {
      releaseTemplatePreviewSlot(teardownPreviewVideo);
    };
  }, [teardownPreviewVideo]);

  useEffect(() => {
    setIsCoverReady(false);
    if (coverRef.current?.complete) {
      setIsCoverReady(true);
    }
  }, [coverUrl]);

  const onMouseEnter = useCallback(() => setIsHovering(true), []);
  const onMouseLeave = useCallback(() => {
    setIsHovering(false);
    releaseTemplatePreviewSlot(teardownPreviewVideo);
  }, [teardownPreviewVideo]);

  const onCoverLoad = useCallback(() => setIsCoverReady(true), []);
  const onCoverError = useCallback(() => setIsCoverReady(true), []);

  const onVideoPlaying = useCallback(() => setIsVideoReady(true), []);
  const onVideoError = useCallback(() => setHasVideoError(true), []);

  return {
    videoRef,
    coverRef,
    isCoverReady,
    shouldAttachVideo,
    showVideo,
    showLoadingOverlay,
    hasVideoError,
    onMouseEnter,
    onMouseLeave,
    onCoverLoad,
    onCoverError,
    onVideoPlaying,
    onVideoError,
  };
}
